import { useMemo, useContext, type PropsWithChildren } from 'react'
import { GamificationContext } from '../../../context/gamificationContextCore'
import { PreviewContext } from '../../../context/previewContextCore'
import { ORBIT_SKIN_COSMETIC_TO_KEY, type OrbitSkinCosmeticId } from '../../../assets/cosmetics'
import {
  OrbitSkinContext,
  type OrbitSkinContextValue,
  type OrbitSkinId,
} from './orbitSkinCore'

export type { OrbitSkinId, OrbitSkinContextValue }

export const OrbitSkinProvider = ({ children }: PropsWithChildren) => {
  // Read the underlying contexts directly (rather than the hook wrappers that throw)
  // so this provider works even when Gamification/Preview aren't mounted — tests, unauthed
  // preview, mock mode all boot without those providers.
  const gami = useContext(GamificationContext)
  const preview = useContext(PreviewContext)
  const profile = gami?.profile ?? null
  const activePreview = preview?.activePreview ?? null

  const skin = useMemo<OrbitSkinId>(() => {
    // Preview beats profile — matches THEME preview flow.
    if (activePreview?.kind === 'ORBIT') {
      const cosmeticId = activePreview.cosmeticId as OrbitSkinCosmeticId
      const key = ORBIT_SKIN_COSMETIC_TO_KEY[cosmeticId]
      if (key) return key
    }
    const equipped = profile?.equippedOrbitSkinId as OrbitSkinCosmeticId | undefined
    return (equipped && ORBIT_SKIN_COSMETIC_TO_KEY[equipped]) ?? 'cream'
  }, [activePreview, profile?.equippedOrbitSkinId])

  const value = useMemo<OrbitSkinContextValue>(() => ({ skin }), [skin])
  return <OrbitSkinContext.Provider value={value}>{children}</OrbitSkinContext.Provider>
}
